import { useState, type FormEvent } from 'react';
import { api } from '../api';
import { ChipToggle, ErrorBox, Field, Loading, PageHead, Panel, toast } from '../components/ui';
import { clampPct, dateLabel, dday, errorMessage } from '../format';
import { useApi } from '../hooks';
import { BIAS, QUALITY } from '../labels';
import { go } from '../router';
import type { BiasTag, PredictionDetail } from '../types';
import { StatusBadge } from './Decisions';

const pctOf = (p: number) => `${Math.round(p * 100)}%`;

function quadrant(p: PredictionDetail): string | null {
  if (p.kind !== 'decision' || p.decision_quality == null || p.decision_quality === 3 || p.outcome == null) return null;
  const good = p.decision_quality >= 4;
  if (good) return p.outcome === 1 ? '실력 (좋은 판단 + 좋은 결과)' : '불운 (좋은 판단 + 나쁜 결과)';
  return p.outcome === 1 ? '행운 — 위험 신호 (나쁜 판단 + 좋은 결과)' : '당연한 결과 (나쁜 판단 + 나쁜 결과)';
}

function ResultSummary({ p }: { p: PredictionDetail }) {
  const q = quadrant(p);
  return (
    <>
      <div className="prob-hero">
        <StatusBadge p={p} />
        {p.status === 'resolved' && (
          <span>
            결과: <b>{p.outcome === 1 ? '예 (일어남)' : '아니오'}</b>
          </span>
        )}
        {p.brier != null && p.outcome != null && (
          <span className="muted small">
            브라이어 = ({p.prob_current.toFixed(2)} − {p.outcome})² = <b>{p.brier.toFixed(3)}</b>
          </span>
        )}
      </div>
      {p.outcome_note && <section className="facet"><h3>실제로 있었던 일</h3><p className="pre">{p.outcome_note}</p></section>}
      {p.decision_quality != null && (
        <section className="facet">
          <h3>판단 과정의 질</h3>
          <p>{p.decision_quality} / 5 · {QUALITY[p.decision_quality]}{q && ` → ${q}`}</p>
        </section>
      )}
      {p.bias_tags.length > 0 && (
        <section className="facet">
          <h3>돌아본 편향</h3>
          <div className="chips">{p.bias_tags.map((b) => <span key={b} className="badge">{BIAS[b]}</span>)}</div>
        </section>
      )}
      {p.lesson && <section className="facet"><h3>교훈</h3><p className="pre">{p.lesson}</p></section>}
    </>
  );
}

export function DecisionDetail({ id }: { id: number }) {
  const { data, error, reload, setData } = useApi(() => api.prediction(id), [id]);
  const [newProb, setNewProb] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState<0 | 1 | null | undefined>(undefined);
  const [outcomeNote, setOutcomeNote] = useState('');
  const [quality, setQuality] = useState<number | null>(null);
  const [bias, setBias] = useState<BiasTag[]>([]);
  const [lesson, setLesson] = useState('');
  const [editing, setEditing] = useState(false);
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const p = data;

  const doUpdate = async (e: FormEvent) => {
    e.preventDefault();
    if (newProb == null) return;
    try {
      setData(await api.updateProbability(id, newProb / 100, note));
      setNewProb(null);
      setNote('');
      toast('확률을 수정했습니다 (이력에 남음)');
    } catch (err) {
      toast(errorMessage(err), 'err');
    }
  };
  const doResolve = async (e: FormEvent) => {
    e.preventDefault();
    if (outcome === undefined) {
      toast('결과(예/아니오/무효)를 고르세요', 'err');
      return;
    }
    try {
      setData(await api.resolve(id, { outcome, outcome_note: outcomeNote, decision_quality: quality, bias_tags: bias, lesson }));
      setEditing(false);
      toast('결과를 기록했습니다');
    } catch (err) {
      toast(errorMessage(err), 'err');
    }
  };
  const startEdit = () => {
    setOutcome(p.status === 'void' ? null : p.outcome);
    setOutcomeNote(p.outcome_note);
    setQuality(p.decision_quality);
    setBias(p.bias_tags);
    setLesson(p.lesson);
    setEditing(true);
  };
  const remove = async () => {
    if (!window.confirm('이 예측을 지울까요? 기록을 지우면 브라이어 점수에서도 빠집니다.')) return;
    try {
      await api.deletePrediction(id);
      toast('지웠습니다');
      go('/decisions');
    } catch (err) {
      toast(errorMessage(err), 'err');
    }
  };
  const showResolve = p.status === 'open' || editing;

  return (
    <>
      <PageHead
        title={p.question}
        sub={`${p.kind === 'decision' ? '결정' : '예측'} · 기록 ${dateLabel(p.created_at)} · 마감 ${dateLabel(p.resolve_by)}${p.status === 'open' ? ` (${dday(p.days_left)})` : ''}`}
        actions={<button type="button" className="btn btn-danger" onClick={() => void remove()}>지우기</button>}
      />
      <div className="detail-grid">
        <div className="stack-col">
          <Panel>
            <div className="prob-hero">
              <span className="prob big">{pctOf(p.prob_current)}</span>
              <span className="muted">{p.prob_initial !== p.prob_current ? `처음 ${pctOf(p.prob_initial)}에서 수정` : '처음 확률 그대로'}</span>
              {p.base_rate != null && <span className="badge">기준율 {pctOf(p.base_rate)}</span>}
            </div>
            {p.criteria && <section className="facet"><h3>판정 기준</h3><p className="pre">{p.criteria}</p></section>}
            {p.kind === 'decision' && (p.options || p.choice) && (
              <section className="facet">
                <h3>선택지 → 고른 것</h3>
                {p.options && <p className="pre">{p.options}</p>}
                {p.choice && <p><b>→ {p.choice}</b></p>}
              </section>
            )}
            {p.reasoning && <section className="facet"><h3>판단 근거</h3><p className="pre">{p.reasoning}</p></section>}
            {p.premortem && <section className="facet"><h3>사전 부검 (틀린다면)</h3><p className="pre">{p.premortem}</p></section>}
          </Panel>

          {p.status !== 'open' && !editing && (
            <Panel title="결과와 사후 분석" action={<button type="button" className="btn btn-ghost btn-sm" onClick={startEdit}>고치기</button>}>
              <ResultSummary p={p} />
            </Panel>
          )}

          {showResolve && (
            <Panel title={p.status === 'open' ? '결과 입력' : '결과 · 사후 분석 고치기'}>
              <form onSubmit={doResolve}>
                <Field label="결과">
                  <div className="chips" role="group" aria-label="결과">
                    <ChipToggle on={outcome === 1} onClick={() => setOutcome(1)}>예 (일어남)</ChipToggle>
                    <ChipToggle on={outcome === 0} onClick={() => setOutcome(0)}>아니오</ChipToggle>
                    <ChipToggle on={outcome === null} onClick={() => setOutcome(null)}>무효 (판정 불가)</ChipToggle>
                  </div>
                </Field>
                <Field label="실제로 무슨 일이 있었나" htmlFor="r-note">
                  <textarea id="r-note" className="input" rows={2} value={outcomeNote} onChange={(e) => setOutcomeNote(e.target.value)} />
                </Field>
                <Field label="판단 과정의 질 (결과와 따로)" hint="그때 알 수 있던 정보로 합리적으로 판단했나? 결과가 좋아도 과정은 나빴을 수 있습니다">
                  <div className="chips">
                    {[1, 2, 3, 4, 5].map((v) => (
                      <ChipToggle key={v} on={quality === v} onClick={() => setQuality(quality === v ? null : v)}>{v} {QUALITY[v]}</ChipToggle>
                    ))}
                  </div>
                </Field>
                <Field label="돌아보니 있었던 편향 (여러 개 가능)">
                  <div className="chips">
                    {(Object.keys(BIAS) as BiasTag[]).map((b) => (
                      <ChipToggle key={b} on={bias.includes(b)} onClick={() => setBias(bias.includes(b) ? bias.filter((x) => x !== b) : [...bias, b])}>
                        {BIAS[b]}
                      </ChipToggle>
                    ))}
                  </div>
                </Field>
                <Field label="다음에 바꿀 한 가지 (교훈)" htmlFor="r-lesson">
                  <textarea id="r-lesson" className="input" rows={2} value={lesson} onChange={(e) => setLesson(e.target.value)} />
                </Field>
                <div className="form-actions">
                  {editing && <button type="button" className="btn" onClick={() => setEditing(false)}>취소</button>}
                  <button type="submit" className="btn btn-primary">결과 기록</button>
                </div>
              </form>
            </Panel>
          )}
        </div>

        <div className="stack-col">
          <Panel title="확률 변화">
            <ol className="timeline">
              <li>
                <b>{pctOf(p.prob_initial)}</b> <span className="muted small">{dateLabel(p.created_at)} · 처음</span>
              </li>
              {p.updates.map((u) => (
                <li key={u.id}>
                  <b>{pctOf(u.probability)}</b> <span className="muted small">{dateLabel(u.created_at)}</span>
                  <p className="small">{u.note}</p>
                </li>
              ))}
            </ol>
            {p.status === 'open' && (
              <form onSubmit={doUpdate} className="update-form">
                <p className="small muted">새 정보가 생겼을 때만, 이유와 함께 고치세요. 처음 확률과 이력은 지워지지 않습니다.</p>
                <input
                  className="input narrow" type="number" min={1} max={99} placeholder="새 %" aria-label="새 확률 (%)"
                  value={newProb ?? ''} onChange={(e) => setNewProb(e.target.value ? clampPct(Number(e.target.value)) : null)}
                />
                <input className="input" placeholder="고치는 이유 (필수)" aria-label="고치는 이유" value={note} onChange={(e) => setNote(e.target.value)} />
                <button className="btn" type="submit" disabled={newProb == null || !note.trim()}>확률 고치기</button>
              </form>
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}
